import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled, { css, keyframes } from 'styled-components';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import Button from './Button';
import UploadDropAreaSVG from '../../assets/upload-drop-area.svg';

const pulse = keyframes`
	0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.3); }
	70% { box-shadow: 0 0 0 8px rgba(33, 150, 243, 0); }
	100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const Wrapper = styled.div(
	({ theme }) => css`
		align-items: center;
		background-color: ${theme.palette.background.paper};
		border-radius: 16px;
		box-sizing: border-box;
		display: flex;
		justify-content: center;
		min-height: 260px;
		padding: ${theme.spacing(4)};
		transition: all 0.3s ease;
		width: 100%;
	`,
);

const DashedSubWrapper = styled.div<{ isActive?: boolean }>(
	({ theme, isActive }) => css`
		align-items: center;
		background-color: ${isActive ? theme.palette.action.hover : theme.palette.background.default};
		border: 2px dashed ${isActive ? theme.palette.primary.main : theme.palette.divider};
		border-radius: 16px;
		box-sizing: border-box;
		cursor: pointer;
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(2)};
		height: 100%;
		justify-content: center;
		min-height: 220px;
		transition: all 0.25s ease-in-out;
		width: 100%;
		${isActive &&
		css`
			animation: ${pulse} 1.2s ease-in-out infinite;
			transform: scale(1.01);
		`}

		&:hover {
			background-color: ${theme.palette.action.hover};
			border-color: ${theme.palette.primary.light};
		}

		img {
			height: 90px;
			opacity: 0.8;
			user-select: none;
			width: 90px;
		}
		button {
			margin-bottom: ${theme.spacing(2)};
		}
	`,
);

interface UploadDropzoneProps {
	onDrop: (acceptedFiles: File[]) => void;
}

function UploadDropzone({ onDrop }: UploadDropzoneProps) {
	const onDropCB = useCallback(onDrop, [onDrop]);
	const { enqueueSnackbar } = useSnackbar();

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		multiple: false,
		accept: {
			'text/xml': ['.xml'],
		},
		onDrop: (acceptedFiles) => {
			const validFiles = acceptedFiles.filter((file) => file.type !== '' || file.size > 0);

			onDropCB(validFiles);
		},
		onDropRejected: (fileRejections) => {
			const reason = fileRejections[0]?.errors[0]?.code;
			switch (reason) {
				case 'file-invalid-type':
					if (fileRejections.length > 1)
						enqueueSnackbar('Folders cannot be uploaded. Please select a valid XML file.', { variant: 'warning' });
					else enqueueSnackbar('Only XML files are accepted.', { variant: 'warning' });
					break;
				case 'too-many-files':
					enqueueSnackbar('Please drop only one file at a time.', { variant: 'warning' });
					break;
				default:
					enqueueSnackbar('Invalid file or folder. Please select a valid XML file.', {
						variant: 'warning',
					});
			}
		},
	});

	return (
		<Wrapper {...getRootProps()}>
			<DashedSubWrapper isActive={isDragActive}>
				<input {...getInputProps()} />
				<img src={UploadDropAreaSVG} alt="Upload area" />

				{isDragActive ? (
					<Typography variant="bodyLargeBold" color="primary">
						Drop your file here
					</Typography>
				) : (
					<>
						<Typography variant="bodyLargeBold">Drag and drop your MEC XML file</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center', px: 2 }}>
							or select a file from your computer
						</Typography>
					</>
				)}

				<Button nxstyle="secondary-black">Select File</Button>
			</DashedSubWrapper>
		</Wrapper>
	);
}

export default UploadDropzone;
